<template>
  <atom-info-box>
    When you are sent Contributions & QAs, you can choose whether to receive attached documents as a PDF or in their original format.
  </atom-info-box>
  <atom-radio
    id="document-format"
    v-model="documentDownloadPreferencesOption"
    label="Document Format"
    :options="documentDownloadPreferenceOptions"
    :error-message="validationErrors.documentDownloadPreference"
  />
  <atom-button label="Save" type="primary" :disabled="saveDisabled" @click.prevent="saveUserDocumentPreference"/>
</template>

<script lang="ts">
import type { AjaxError } from "core/js/spring-ajax";
import type { DocumentDownloadPreference, DocumentDownloadPreferenceOption } from "referrals/js/enum/document-download-preference";
import { useVuelidate } from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import AtomButton from "core/components/atom/button/AtomButton.vue";
import AtomInfoBox from "core/components/atom/infoBox/AtomInfoBox.vue";
import AtomRadio from "core/components/atom/radio/AtomRadio.vue";
import { showChangesSavedFlash, showValidationFlash } from "core/js/flashmessage/flash-message";
import { getFirstValidationError } from "core/js/validation/validation-messages";
import { getDocumentDownloadPreferenceFormatOptions } from "referrals/js/enum/document-download-preference";
import { useUserDocumentPreferenceStore } from "referrals/store/user-document-preferences.store";
import { defineComponent } from "vue";

type UserDocumentPreferenceValidationErrors = {
  documentDownloadPreference?: string,
};

export default defineComponent({
  components: { AtomButton, AtomRadio, AtomInfoBox },
  setup() {
    return {
      userDocumentPreferenceStore: useUserDocumentPreferenceStore(),
      v$: useVuelidate(),
    };
  },
  data(): {
    documentDownloadPreference: DocumentDownloadPreference | undefined,
    saveDisabled: boolean,
    validationErrors: UserDocumentPreferenceValidationErrors,
  } {
    return {
      documentDownloadPreference: undefined,
      saveDisabled: false,
      validationErrors: {
        documentDownloadPreference: "",
      },
    };
  },
  computed: {
    documentDownloadPreferenceOptions(): Array<DocumentDownloadPreferenceOption> {
      return getDocumentDownloadPreferenceFormatOptions();
    },
    documentDownloadPreferencesOption: {
      get(): DocumentDownloadPreferenceOption | undefined {
        return this.documentDownloadPreferenceOptions.find(option => option.id === this.documentDownloadPreference);
      },
      set(documentDownloadPreferenceOption: DocumentDownloadPreferenceOption) {
        this.documentDownloadPreference = documentDownloadPreferenceOption.id;
      },
    },
  },
  async created() {
    await this.userDocumentPreferenceStore.isReady();
    this.documentDownloadPreference = this.userDocumentPreferenceStore.userDocumentPreference?.documentDownloadPreference;
  },
  methods: {
    async saveUserDocumentPreference() {
      this.resetValidationErrors();
      if (!await this.v$.$validate()) {
        this.validationErrors.documentDownloadPreference = getFirstValidationError(this.v$.documentDownloadPreference, "Document Format");
        showValidationFlash();
        return;
      }

      this.saveDisabled = true;

      try {
        await this.userDocumentPreferenceStore.updateUserDocumentPreference(this.documentDownloadPreference!);
        showChangesSavedFlash();
      } catch (error: unknown) {
        const ajaxError = error as AjaxError;
        if (ajaxError.status === 422) {
          this.validationErrors = ajaxError.responseJSON;
          showValidationFlash();
        } else {
          throw error;
        }
      } finally {
        this.saveDisabled = false;
      }
    },
    resetValidationErrors() {
      this.validationErrors = {
        documentDownloadPreference: "",
      };
    },
  },
  validations() {
    return {
      documentDownloadPreference: { required },
    };
  },
});
</script>
